import { AxiosError, AxiosResponse } from 'axios'
import { Context } from '../server-middleware/context'

export interface User {
  avatar: string
  userid: number
  username: string
  usertitle: string
}

const fallback: (userId: number) => User = (userId) => ({
  avatar: '',
  userid: userId,
  username: '',
  usertitle: ''
})

class UserAPI {
  url: string
  context: Context
  cache: {[userId: number]: User | undefined}

  constructor (context: Context) {
    this.context = context
    this.cache = {}
    const host = process.env.app_config_api_host
    this.url = host ? `https://${host}` : 'http://api.dev.insided.com'
  }

  public async getUser (userId: number): Promise<User> {
    if (!userId) {
      return fallback(0)
    }

    const cached = this.cache[userId]
    if (cached) {
      return cached
    }

    try {
      const res: AxiosResponse<User> = await this.context.axios.get(`${this.url}/user/${userId}`, {
        headers: {
          'X-Consumer-Custom-Id': this.context.yipId
        }
      })
      this.cache[userId] = res.data
      return res.data
    } catch (e) {
      const err: AxiosError = e
      this.context.logger(err)

      return fallback(userId)
    }
  }

  public async getUsers (userIds: number[]): Promise<Record<string, User|undefined>> {
    const uncachedIds = userIds.filter(id => !this.cache[id])

    if (!uncachedIds) return this.cache

    try {
      const q = uncachedIds.map(uid => `filter%5Bt.userid%5D%5B%5D=${uid}`).join('&')
      const res: AxiosResponse<Record<string, User>> = await this.context.axios.get(`${this.url}/crud/viewuser?${q}`, {
        headers: {
          'X-Consumer-Custom-Id': this.context.yipId
        }
      })
      Object.keys(res.data).forEach(key => {
        const u = res.data[key]
        this.cache[u.userid] = {
          avatar: u.avatar,
          userid: u.userid,
          username: u.username,
          usertitle: u.usertitle
        }
      })
      return this.cache
    } catch (e) {
      const err: AxiosError = e
      this.context.logger(err)

      return this.cache
    }
  }
}

export default UserAPI
